import { memo, useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { DAY_KEYS, DAY_LABELS } from "@/lib/weekly-plan";
import { useDayTypeSchedule, parseMinutes } from "@/lib/day-type-schedule";

// ── Day-type defaults ─────────────────────────────────────────────────────────

const FERIADO_DEFAULTS = [
  { id: "f1", time: "6:00 AM",  activity: "Devocional personal"                 },
  { id: "f2", time: "8:00 AM",  activity: "Desayuno con la familia"             },
  { id: "f3", time: "10:00 AM", activity: "Actividades personales / recreación" },
  { id: "f4", time: "1:00 PM",  activity: "Almuerzo y descanso"                 },
  { id: "f5", time: "4:00 PM",  activity: "Tiempo libre / paseo"                },
  { id: "f6", time: "8:00 PM",  activity: "Familia / descanso nocturno"         },
];
const HABIL_DEFAULTS = [
  { id: "h1", time: "6:00 AM",  activity: "Devocional y oración"                },
  { id: "h2", time: "8:00 AM",  activity: "Visitas pastorales"                  },
  { id: "h3", time: "10:00 AM", activity: "Estudios bíblicos / seguimiento"     },
  { id: "h4", time: "1:00 PM",  activity: "Almuerzo"                            },
  { id: "h5", time: "3:00 PM",  activity: "Administración y correspondencia"    },
  { id: "h6", time: "7:00 PM",  activity: "Culto o reunión de iglesia"          },
];
const DOMINGO_DEFAULTS = [
  { id: "d1", time: "6:00 AM",  activity: "Preparación personal"                },
  { id: "d2", time: "9:00 AM",  activity: "Escuela Sabática / clubes"           },
  { id: "d3", time: "11:00 AM", activity: "Culto de adoración"                  },
  { id: "d4", time: "1:00 PM",  activity: "Confraternización / almuerzo"        },
  { id: "d5", time: "3:00 PM",  activity: "Actividades juveniles o especiales"  },
  { id: "d6", time: "6:00 PM",  activity: "Actividades vespertinas / evangelismo"},
];

// ── 7-day type objects (module-level = stable references) ─────────────────────
const DAY_TYPES_7 = {
  domingo:   { id: "domingo",   defaults: DOMINGO_DEFAULTS  },
  lunes:     { id: "lunes",     defaults: FERIADO_DEFAULTS  },
  martes:    { id: "martes",    defaults: HABIL_DEFAULTS    },
  miercoles: { id: "miercoles", defaults: HABIL_DEFAULTS    },
  jueves:    { id: "jueves",    defaults: HABIL_DEFAULTS    },
  viernes:   { id: "viernes",   defaults: HABIL_DEFAULTS    },
  sabado:    { id: "sabado",    defaults: []                },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function nowMinutes() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

// ── Block row ─────────────────────────────────────────────────────────────────

const BlockRow = memo(function BlockRow({ block, status, isLast }) {
  const isPast    = status === "past";
  const isCurrent = status === "current";

  return (
    <div className={`flex gap-3 ${isLast ? "" : "pb-2.5"}`}>

      {/* Time + connector */}
      <div className="flex flex-col items-center shrink-0" style={{ width: 60 }}>
        <span className={`text-[11px] font-bold tabular-nums leading-none whitespace-nowrap
          ${isCurrent
            ? "text-[#1b4f9c] dark:text-blue-400"
            : isPast
            ? "text-slate-300 dark:text-slate-600"
            : "text-slate-400 dark:text-slate-500"
          }`}
        >
          {block.time || "—"}
        </span>
        {!isLast && (
          <div className={`flex-1 w-px my-1.5 min-h-[10px] ${
            isPast ? "bg-emerald-200 dark:bg-emerald-800/50" : "bg-slate-100 dark:bg-slate-700/40"
          }`} />
        )}
      </div>

      {/* Activity card */}
      <div className={`flex-1 min-w-0 rounded-[14px] px-3 py-2 flex items-center gap-2 transition-all duration-300
        ${isCurrent
          ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200/60 dark:border-blue-700/50 shadow-[0_1px_6px_rgba(27,79,156,0.08)]"
          : "bg-white dark:bg-slate-800 border border-slate-200/40 dark:border-slate-700/40 shadow-[0_1px_4px_rgba(0,0,0,0.025)]"
        }
        ${isPast ? "opacity-45" : ""}
      `}>
        <div className="shrink-0 h-5 w-5 flex items-center justify-center">
          {isPast ? (
            <Check size={11} className="text-emerald-500" strokeWidth={2.5} />
          ) : isCurrent ? (
            <span className="h-2 w-2 rounded-full bg-[#1b4f9c] dark:bg-blue-400 animate-pulse" />
          ) : (
            <span className="h-1.5 w-1.5 rounded-full bg-slate-200 dark:bg-slate-600" />
          )}
        </div>
        <p className={`flex-1 min-w-0 text-[13px] leading-snug truncate
          ${isCurrent
            ? "font-semibold text-[#1b4f9c] dark:text-blue-300"
            : isPast
            ? "font-medium text-slate-400 dark:text-slate-500"
            : "font-medium text-slate-700 dark:text-slate-200"
          }`}
        >
          {block.activity || <span className="italic text-slate-300 dark:text-slate-600 font-normal">Sin actividad</span>}
        </p>
      </div>
    </div>
  );
});

// ── Main component ────────────────────────────────────────────────────────────

export default function PastoralPlanToday() {
  const today      = new Date();
  const dayOfWeek  = today.getDay();
  const todayKey   = DAY_KEYS[dayOfWeek];
  const todayLabel = DAY_LABELS[todayKey];
  const dayType = DAY_TYPES_7[todayKey];

  const { blocks } = useDayTypeSchedule(dayType);

  const [currentMinutes, setCurrentMinutes] = useState(nowMinutes);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setCurrentMinutes(nowMinutes()), 60_000);
    return () => clearInterval(id);
  }, []);

  const blocksWithStatus = useMemo(() => {
    const timed = blocks.map((b) => ({ ...b, mins: parseMinutes(b.time) }));
    let currentIdx = -1;
    timed.forEach((b, i) => {
      if (b.mins !== 9999 && b.mins <= currentMinutes) currentIdx = i;
    });
    return timed.map((b, i) => ({
      ...b,
      status: i < currentIdx ? "past" : i === currentIdx ? "current" : "future",
    }));
  }, [blocks, currentMinutes]);

  // ── Derived slices ─────────────────────────────────────────────────────────
  const pastBlocks   = blocksWithStatus.filter(b => b.status === "past");
  const currentBlock = blocksWithStatus.find(b => b.status === "current") || null;
  const futureBlocks = blocksWithStatus.filter(b => b.status === "future");
  const allDone      = blocksWithStatus.length > 0 && futureBlocks.length === 0 && !currentBlock;

  // Compact view: current + next 2 (or first 3 if nothing started yet)
  const compactBlocks = useMemo(() => {
    if (allDone) return pastBlocks.slice(-1);
    const visible = [];
    if (currentBlock) visible.push(currentBlock);
    visible.push(...futureBlocks.slice(0, 2));
    if (visible.length === 0) return futureBlocks.slice(0, 3);
    return visible;
  }, [allDone, currentBlock, futureBlocks, pastBlocks]);

  const hasHiddenBlocks = pastBlocks.length > 0 || futureBlocks.length > 2;

  const displayBlocks = expanded ? blocksWithStatus : compactBlocks;

  return (
    <div className="px-4">
      <div className="bg-white dark:bg-slate-800 rounded-[24px] border border-slate-200/50 dark:border-slate-700/60 shadow-[0_2px_12px_rgba(0,0,0,0.03)] overflow-hidden">

        {/* ── Header ── */}
        <div className="flex items-center gap-3 px-4 pt-3.5 pb-3">
          <span className="text-[14px] font-semibold text-slate-800 dark:text-slate-100">
            {todayLabel}
          </span>
        </div>

        {/* ── Past summary (compact only) ── */}
        {!expanded && pastBlocks.length > 0 && !allDone && (
          <div className="px-4 pb-2 flex items-center gap-2.5">
            <div className="flex items-center gap-1.5 shrink-0">
              <Check size={10} className="text-emerald-500" strokeWidth={2.5} />
              <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                {pastBlocks.length} {pastBlocks.length === 1 ? "completada" : "completadas"}
              </span>
            </div>
            <div className="flex-1 h-px bg-slate-100 dark:bg-slate-700/40" />
          </div>
        )}

        {/* ── All done message ── */}
        {allDone && !expanded && (
          <div className="px-4 pb-2 flex items-center gap-2.5">
            <div className="flex items-center gap-1.5 shrink-0">
              <Check size={10} className="text-emerald-500" strokeWidth={2.5} />
              <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                Día completado · {pastBlocks.length} actividades
              </span>
            </div>
            <div className="flex-1 h-px bg-emerald-100 dark:bg-emerald-800/30" />
          </div>
        )}

        {/* ── Timeline ── */}
        {blocksWithStatus.length === 0 ? (
          <p className="text-[12px] text-slate-400 dark:text-slate-500 text-center py-3 px-4 italic">
            Sin bloques configurados
          </p>
        ) : (
          <div className="px-4 pb-3">
            {displayBlocks.map((block, i) => (
              <BlockRow
                key={block.id}
                block={block}
                status={block.status}
                isLast={i === displayBlocks.length - 1}
              />
            ))}
          </div>
        )}

        {/* ── Expand / collapse toggle ── */}
        {hasHiddenBlocks && (
          <button
            onClick={() => setExpanded(e => !e)}
            className="w-full flex items-center justify-center gap-1 py-2 border-t border-slate-100 dark:border-slate-700/40 text-[11px] font-medium text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-150 active:opacity-70"
          >
            {expanded
              ? <><ChevronUp size={12} /> Ver menos</>
              : <><ChevronDown size={12} /> Ver día completo</>
            }
          </button>
        )}

      </div>
    </div>
  );
}
